import 'dayjs/locale/de';
import 'dayjs/locale/de-ch';
import 'dayjs/locale/fr';
import 'dayjs/locale/fr-ch';
import 'dayjs/locale/it';
import 'dayjs/locale/it-ch';
import 'normalize.css';
import * as Sentry from '@sentry/browser';
import { API_BASE_URL, APP_POOL_CONFIG, ENVIRONMENT, POOL_INSTANCE, RELEASE, SENTRY_DSN } from '@synergy/config';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { ErrorBoundary, FilterDisplayCache } from '@synergy/components';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';
import { Locale } from '@synergy/models';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Provider } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider } from '@mui/material/styles';
import { createRoot } from 'react-dom/client';
import { deDE, frFR, itIT } from '@mui/x-date-pickers/locales';
import { initSentry, sentrySetUser } from '@synergy/utils';
import { paloTheme } from '@synergy/theme-palo';
import { sharedMessagesDE, sharedMessagesDE_CH, sharedMessagesFR, sharedMessagesIT } from '@synergy/translations';
import dayjs from 'dayjs';

import './common/style/index.css';
import * as serviceWorker from './serviceWorker';
import { Api } from './api';
import { App } from './App';
import { configureStore } from './store/legacy-configure-store';
import messagesDE from './translations/de.json';
import messagesDE_CH from './translations/de-ch.json';
import messagesFR from './translations/fr.json';
import messagesIT from './translations/it.json';

initSentry({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    project: 'customer-frontend',
    poolInstance: POOL_INSTANCE,
});

const headScriptElement = document.createElement('script');

headScriptElement.src = `${API_BASE_URL}/head-script/customer`;
headScriptElement.type = 'text/javascript';

document.head.appendChild(headScriptElement);
document.title = `${APP_POOL_CONFIG.name_short} Customer`;

const messages: { [locale: string]: { [key: string]: string } } = {
    de: { ...sharedMessagesDE, ...messagesDE },
    'de-ch': { ...sharedMessagesDE, ...sharedMessagesDE_CH, ...messagesDE, ...messagesDE_CH },
    fr: { ...sharedMessagesFR, ...messagesFR },
    'it-ch': { ...sharedMessagesIT, ...messagesIT },
};

const api = new Api(API_BASE_URL);

const start = async () => {
    const baseState = await api.common.getBaseState();

    if (!baseState?.user || !baseState.account) {
        return;
    }

    sentrySetUser({
        id: baseState.user.id.toString(),
    });

    if (baseState.user && baseState.account) {
        // Dispatch user/account details for usage in external scripts (loaded with the `head-tags` script)
        window.dispatchEvent(
            new CustomEvent('synergyUserLoaded', {
                detail: { user: baseState.user, account: baseState.account, project: 'customer' },
            })
        );
    }

    const defaultLocale = baseState.currentLocale.replace('_', '-');
    const locale = baseState.user?.locale.replace('_', '-') || defaultLocale;
    const pickerLocale = locale === Locale.French ? frFR : locale === Locale.Italien ? itIT : deDE;
    const store = configureStore(api, baseState);

    dayjs.locale(locale);
    const mergedMessages = { ...messages[defaultLocale], ...messages[locale] }; // Merge default messages with selected locale
    const topLevelLocale = locale.split('-')[0];

    const app = (
        <HelmetProvider>
            <ErrorBoundary errorMessage={<span onClick={() => Sentry.showReportDialog()}>Report feedback</span>}>
                <Helmet>
                    <html lang={topLevelLocale} />
                </Helmet>
                <Provider store={store}>
                    <IntlProvider locale={locale} messages={mergedMessages} defaultLocale={defaultLocale}>
                        <StyledEngineProvider injectFirst={true}>
                            <LocalizationProvider
                                localeText={pickerLocale.components.MuiLocalizationProvider.defaultProps.localeText}
                                dateAdapter={AdapterDayjs}
                                adapterLocale={topLevelLocale}
                            >
                                <ThemeProvider theme={paloTheme}>
                                    <CssBaseline />
                                    <SnackbarProvider maxSnack={3}>
                                        <FilterDisplayCache>
                                            <App />
                                        </FilterDisplayCache>
                                    </SnackbarProvider>
                                </ThemeProvider>
                            </LocalizationProvider>
                        </StyledEngineProvider>
                    </IntlProvider>
                </Provider>
            </ErrorBoundary>
        </HelmetProvider>
    );

    const rootElement = document.getElementById('root');
    if (!rootElement) {
        return;
    }
    const root = createRoot(rootElement);

    root.render(app);

    // If you want your app to work offline and load faster, you can change
    // unregister() to register() below. Note this comes with some pitfalls.
    // Learn more about service workers: https://bit.ly/CRA-PWA
    serviceWorker.unregister();
};

start();
